import { motion, AnimatePresence } from 'motion/react';
import PixelIcon from '@/components/ui/PixelIcon';
import { CARD_DEFINITIONS, RARITY_COLORS } from '@/config/cards';
import { COLOR } from '@/config/designTokens';
import type { PixelIconName } from '@/components/ui/PixelIcon';
import type { CardType, CardRarity } from '@/types/card';

interface PlayedCardInfo {
  type: CardType;
  rarity: CardRarity;
}

interface CardPlayEffectProps {
  playedCard: PlayedCardInfo | null;
}

const CardPlayEffect: React.FC<CardPlayEffectProps> = ({ playedCard }) => {
  const def = playedCard ? CARD_DEFINITIONS[playedCard.type] : null;
  const glowColor = playedCard ? RARITY_COLORS[playedCard.rarity] : COLOR.disabled;

  return (
    <AnimatePresence>
      {playedCard && def && (
        <motion.div
          key={`card-play-${playedCard.type}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9500,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 60%, transparent 85%)',
          }}
        >
          {/* Dramatic flash burst on entry */}
          <motion.div
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: 4, opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${glowColor}60 0%, transparent 70%)`,
              pointerEvents: 'none',
            }}
          />

          {/* Card slamming into center */}
          <motion.div
            initial={{ scale: 0.1, y: 300, rotateZ: -15, opacity: 0 }}
            animate={{ scale: 1, y: 0, rotateZ: 0, opacity: 1 }}
            exit={{ scale: 0.3, y: -80, opacity: 0, rotateZ: 10 }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 22,
              mass: 0.7,
            }}
            style={{
              width: 160,
              height: 210,
              borderRadius: 18,
              border: `3px solid ${glowColor}`,
              background: 'linear-gradient(145deg, rgba(20, 20, 35, 0.95) 0%, rgba(10, 10, 20, 0.98) 100%)',
              backdropFilter: 'blur(12px)',
              boxShadow: `0 0 40px ${glowColor}90, 0 0 80px ${glowColor}50, 0 0 120px ${glowColor}25, inset 0 1px 0 rgba(255,255,255,0.1)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Inner shimmer sweep */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: '200%' }}
              transition={{ duration: 0.8, delay: 0.3, ease: 'easeInOut' }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '50%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
                pointerEvents: 'none',
              }}
            />

            {/* Expanding glow ring */}
            <motion.div
              animate={{ scale: [1, 1.8, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                position: 'absolute',
                inset: -12,
                borderRadius: 26,
                border: `2px solid ${glowColor}`,
                pointerEvents: 'none',
              }}
            />

            {/* Second glow ring offset */}
            <motion.div
              animate={{ scale: [1.2, 2.0, 1.2], opacity: [0.3, 0, 0.3] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
              style={{
                position: 'absolute',
                inset: -18,
                borderRadius: 32,
                border: `1px solid ${glowColor}60`,
                pointerEvents: 'none',
              }}
            />

            {/* Rarity label */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15, duration: 0.2 }}
              style={{
                padding: '2px 10px',
                borderRadius: 6,
                background: `${glowColor}30`,
                border: `1px solid ${glowColor}60`,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                color: glowColor,
              }}
            >
              {playedCard.rarity}
            </motion.div>

            {/* Big icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 15, delay: 0.1 }}
            >
              <PixelIcon
                name={def.icon as PixelIconName}
                size={56}
                color={glowColor}
              />
            </motion.div>

            {/* Card name */}
            <motion.span
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.2 }}
              style={{
                color: '#fff',
                fontSize: 17,
                fontWeight: 800,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                textAlign: 'center',
                lineHeight: 1.2,
                textShadow: `0 0 12px ${glowColor}80`,
              }}
            >
              {def.name}
            </motion.span>
          </motion.div>

          {/* Card description text below */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.25, duration: 0.3 }}
            style={{
              marginTop: 20,
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: 15,
              fontWeight: 500,
              textAlign: 'center',
              textShadow: '0 2px 10px rgba(0,0,0,0.8)',
              maxWidth: 280,
              lineHeight: 1.5,
            }}
          >
            {def.description}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CardPlayEffect;
